import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";
import { useShallow } from "zustand/react/shallow";

/**
 * Sidebar viewport modes. Phase 03 collapses the previous "isExpanded +
 * isHovered" duo into a discrete `mode`:
 *  - "expanded": full 252px shell on desktop (≥ xl).
 *  - "collapsed": 64px icon-only rail on desktop.
 *  - drawer/mobile is **not** a mode here — handled via `isMobileOpen` so
 *    desktop preference survives a viewport change.
 *
 * Hover-expand is intentionally not modelled (spec defers it post-V1+).
 */
type SidebarMode = "expanded" | "collapsed";

interface SidebarState {
  mode: SidebarMode;
  isMobileOpen: boolean;
  isMobile: boolean;
  /** Persisted accordion submenu open state, keyed by NavItem.nameKey. */
  openSubmenus: string[];
}

interface SidebarActions {
  setMode: (mode: SidebarMode) => void;
  toggleMode: () => void;
  toggleMobileSidebar: () => void;
  closeMobileSidebar: () => void;
  setIsMobile: (isMobile: boolean) => void;
  setOpenSubmenus: (values: string[]) => void;
}

type SidebarStore = SidebarState & SidebarActions;

export const useSidebarStore = create<SidebarStore>()(
  persist(
    (set) => ({
      mode: "expanded",
      isMobileOpen: false,
      isMobile: false,
      openSubmenus: [],

      setMode: (mode) => set({ mode }),
      toggleMode: () =>
        set((state) => ({ mode: state.mode === "expanded" ? "collapsed" : "expanded" })),

      toggleMobileSidebar: () =>
        set((state) => ({ isMobileOpen: !state.isMobileOpen })),
      closeMobileSidebar: () => set({ isMobileOpen: false }),

      setIsMobile: (isMobile) =>
        set((state) => ({
          isMobile,
          // Closing the mobile drawer when crossing into desktop avoids a
          // stuck-open drawer after a window resize.
          isMobileOpen: isMobile ? state.isMobileOpen : false,
        })),

      setOpenSubmenus: (values) => set({ openSubmenus: values }),
    }),
    {
      name: "admin-react-sidebar",
      storage: createJSONStorage(() => sessionStorage),
      partialize: (s) => ({ mode: s.mode, openSubmenus: s.openSubmenus }),
    }
  )
);

export const useSidebarMode = () => useSidebarStore((s) => s.mode);
export const useIsMobileOpen = () => useSidebarStore((s) => s.isMobileOpen);
export const useIsMobile = () => useSidebarStore((s) => s.isMobile);
export const useOpenSubmenus = () => useSidebarStore((s) => s.openSubmenus);

export const useSidebarActions = () =>
  useSidebarStore(
    useShallow((s) => ({
      setMode: s.setMode,
      toggleMode: s.toggleMode,
      toggleMobileSidebar: s.toggleMobileSidebar,
      closeMobileSidebar: s.closeMobileSidebar,
      setIsMobile: s.setIsMobile,
      setOpenSubmenus: s.setOpenSubmenus,
    }))
  );

/**
 * Back-compat alias. Old call sites use `useIsExpanded()`; new code should
 * read `useSidebarMode()` and compare directly. Kept until phase 04 sweep.
 * @deprecated Use `useSidebarMode()` instead.
 */
export const useIsExpanded = () =>
  useSidebarStore((s) => (s.isMobile ? false : s.mode === "expanded"));

/**
 * Hover-expand was removed in phase 03; this hook now always returns false.
 * @deprecated Hover-expand is post-V1+; remove call sites.
 */
export const useIsHovered = () => false;
